import {
  Badge,
  BottomNavigation,
  BottomNavigationAction
} from '@material-ui/core'
import { FC, useEffect, useMemo, useState } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { matchPath, useLocation } from 'react-router'
import { MuiClasses } from '../../../types'
import { isNumStep } from '../../../utils/apartments'
import { NavigationItem } from '../types'

interface BottomNavProps extends WithTranslation {
  classes: MuiClasses
  navItems: NavigationItem[]
  doNavigation: (path: string) => void
  currentPathEnd: string
  unreadMessages: number
}

const BottomNav: FC<BottomNavProps> = ({
  classes,
  navItems,
  doNavigation,
  currentPathEnd,
  unreadMessages,
  t
}) => {
  const location = useLocation()
  const matchedUrl: any = matchPath(location.pathname, {
    path: '/users/:userId/apartments/:apartmentId'
  })
  const isContextPath =
    !!matchedUrl?.params?.apartmentId && !!matchedUrl?.params?.userId
  const navDisabled =
    isNumStep(location.pathname, '10') ||
    isNumStep(location.pathname, '12')
  const safeUnreadMessages = Number.isFinite(unreadMessages)
    ? unreadMessages
    : 0

  const itemsToShow = useMemo(
    () =>
      navItems.filter(({ id }) => {
        if (!isContextPath) return false
        if (
          id === '/applications' ||
          id === '/showings/overview' ||
          id === '/overview' ||
          id === '/messages'
        ) {
          return true
        }
        return id.toLowerCase().includes('apartments')
      }),
    [isContextPath, navItems]
  )

  const getInitialValue = () => {
    if (itemsToShow.some(({ id }) => id === currentPathEnd)) {
      return currentPathEnd
    }
    return itemsToShow[0]?.id || ''
  }

  const [value, setValue] = useState<string>(getInitialValue())

  useEffect(() => {
    if (itemsToShow.some(({ id }) => id === currentPathEnd)) {
      setValue(currentPathEnd)
    } else if (itemsToShow[0]?.id) {
      setValue(itemsToShow[0].id)
    }
  }, [currentPathEnd, itemsToShow])

  const renderIcon = (id: string, Icon: any) => {
    const showUnreadBadge =
      id === '/messages' &&
      safeUnreadMessages > 0
    return showUnreadBadge ? (
      <Badge badgeContent={safeUnreadMessages} color='primary'>
        <Icon />
      </Badge>
    ) : (
      <Icon />
    )
  }

  if (!isContextPath || !itemsToShow.length) return null

  return (
    <BottomNavigation
      value={value}
      onChange={(event, newValue) => {
        if (navDisabled) return
        setValue(newValue)
        const isContextualTarget =
          newValue === '/applications' ||
          newValue === '/showings/overview' ||
          newValue === '/overview' ||
          newValue === '/messages'

        if (isContextualTarget && matchedUrl?.url) {
          doNavigation(matchedUrl.url + newValue)
        } else {
          doNavigation(newValue)
        }
      }}
      showLabels
      className={classes.bottomNav}
    >
      {itemsToShow.map(({ id, label, icon: Icon }) => (
        <BottomNavigationAction
          key={id}
          label={t(label)}
          value={id}
          icon={renderIcon(id, Icon)}
          className={classes.bottomNavAction}
          disabled={navDisabled}
        />
      ))}
    </BottomNavigation>
  )
}

export default withTranslation()(BottomNav)
